import { pluralize, singularize } from '@warp-drive/utilities/string';
import { createServer } from 'miragejs';

import factories from '../factories';
import models from '../models';
import serializers from '../serializers';

export default function (config) {
  return createServer({
    ...config,
    models,
    factories,
    serializers,
    inflector: { pluralize, singularize },
    routes,
  });
}

function routes() {
  /* VARIANT RESOURCES
  As we don't have a variant relation, we need to fatch the variants in the frontend. Mirage is ignoring the filter params, so we allways get a variant for each record, which leads to a failing validation of the language. */
  const variantResource = [
    'actions',
    'articles',
    'artists',
    'assets',
    'calendarEvents',
    'categories',
    'collections',
    'jobs',
    'labels',
    'linkPatterns',
    'links',
    'liveEvents',
    'mediaEvents',
    'productFamilies',
    'products',
    'webSeries',
  ];

  variantResource.forEach((variantResource) => {
    this.resource(variantResource);
    this.get(`/${variantResource}`, (schema, request) => {
      const { queryParams } = request;
      const collection = schema[variantResource];

      if (queryParams['filter[cmsVariantParentId]']) {
        return collection.where({
          cmsVariantParentId: queryParams['filter[cmsVariantParentId]'],
        });
      }

      return collection.all();
    });
  });

  /* DEFAULT RESOURCES
  If we don't need search params or any custom logic, we can use the default resource helper. */
  this.resource('a11yScanners', { path: '/a11y/scanners' });
  this.resource('a11yScans', { path: '/a11y/scans' });
  this.resource('assetFormats');
  this.resource('assetFormatRelations');
  this.resource('audioplayers');
  this.resource('audioplayerTracks');
  this.resource('channels');
  this.resource('contests');
  this.resource('contestFormFields');
  this.resource('coopPartners');
  this.resource('customShops');
  this.resource('digitalOrderConfigs');
  this.resource('divisions');
  this.resource('domains');
  this.resource('externalReferences');
  this.resource('externalValues');
  this.resource('frontendUsers');
  this.resource('livePartners');
  this.resource('locations');
  this.resource('mediaEmbeds');
  this.resource('mediaPartners');
  this.resource('newsletterTargets');
  this.resource('organizations');
  this.resource('people');
  this.resource('productContributors');
  this.resource('productElements');
  this.resource('productFamilyContributors');
  this.resource('sections');
  this.resource('shopifyStores');
  this.resource('shortUrls');
  this.resource('tradePartners');
  this.resource('userRoles');
  this.resource('users');
  this.resource('userRoles');

  // Do we need this assetFormatRelations? We have a resource
  this.get('/assetFormatRelations', () => {
    return {
      assetFormatRelations: [
        { id: '1001', label: 'UMG Cropping Format Video 16:9' },
        { id: '1000', label: 'UMG Cropping Format Video 21:9' },
      ],
    };
  });

  this.post('/assetFormatRelations', () => {
    return {
      assetFormatRelation: {
        id: '12345',
        assetID: '12345',
        assetFormatvId: '12345',
        priority: 1,
        width: '1024',
        height: '768',
      },
    };
  });

  /* PAGEBUILDER (MLP)
  For the Page Builder we are using embedded in the serializer. We could also try to use a mirage serializer, but for now it's easier to mock one Page Builder page. */
  this.get('/mlps/:mlp_id', (schema, { params }) => {
    return {
      mlp: {
        id: params.mlp_id,
        headline: 'Super headline',
        hasPublishedMlp: false,
        isMultiArtistPage: false,
        options: {},
        template: false,
        urlPath: '/',
        multiPage: true,
        pages: [
          {
            id: 1,
            name: 'Start',
            position: 0,
            visible: true,
            linkType: 'internal',
            linkTarget: '_self',
            link: '/',
            start: true,
            mlp: params.mlp_id,
            options: [],
            contentItems: [
              {
                id: 2,
                headline: 'test headline',
                entity: null,
                contentItemId: '12',
                type: 'article',
                typeId: 2,
                compositeKey: '2-12',
                children: [],
                parent: null,
              },
            ],
          },
        ],
      },
    };
  });

  /* LOCKING
  We need the lock request, otherwise all forms are disabled */
  this.post('/lockStatus', (schema, request) => {
    return { ...JSON.parse(request.requestBody), status: false };
  });

  this.post('/lock', (schema, request) => {
    return {
      ...JSON.parse(request.requestBody),
      userId: '1',
      userName: 'UserName',
      locktime: new Date(),
      status: true,
    };
  });

  this.post('/unlock', (schema, request) => {
    return { ...JSON.parse(request.requestBody), status: false };
  });

  /* OTHER REQUEST
  In addition to the resources, we also have a few special endpoints for querying or sending data to the REST API */
  this.get('/listDirectories', () => {
    return [];
  });

  this.get('/mlpShops', () => {
    return [];
  });

  this.get('/config/assetFolders', () => {
    return [];
  });

  this.get('/player/encryptData', () => {
    return { encryptedData: '12345' };
  });

  this.post('/assetDownloadLinks', () => {
    return { assetDownloadLink: { id: '12345', accesskey: 'abc123' } };
  });

  this.get('/userInfo', (schema) => {
    return schema.users.find(1);
  });

  this.get('/userActivities', () => {
    return { user: { activityFeed: [] } };
  });

  /* EXTERNAL REQUEST
  We have some external request to the page builder frontend and google APIS. */
  this.get('https://test.pagebuilder-api.universal-music.de/themes', () => {
    return [
      { value: 'default-v1', label: 'Default V1' },
      { value: 'grains', label: 'Grains' },
      { value: 'karussell', label: 'Karussell' },
      { value: 'labsx', label: 'LABSxStudios' },
    ];
  });

  this.get('https://test.pagebuilder-api.universal-music.de/themes/:id', () => {
    return {
      id: 'default-v1',
      label: 'Default V1',
      translations: ['default-v1'],
      channel: 57,
      gtm: { id: 'GTM-123' },
      graphql: { articles: { article: { withProducts: false } } },
      cms: {
        contentItems: {
          'hero-header': true,
          'live-event': true,
          'mlp-column': true,
          'mlp-download-link-form': true,
          'mlp-group': true,
          'mlp-row': true,
          'mlp-text': true,
          'link': true,
          'newsletter': true,
        },
      },
    };
  });

  this.get('https://www.googleapis.com/webfonts/v1/webfonts', () => {
    return {
      kind: 'webfonts#webfontList',
      items: [
        { family: 'ABeeZee' },
        { family: 'Abel' },
        { family: 'Abhaya Libre' },
      ],
    };
  });
}
